import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Loader } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const eventTypeOptions = [
  'Wedding',
  'Nightclub / Bar',
  'Corporate Event',
  'Festival',
  'Art Installation',
  'Private Party',
  'Other',
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.12, duration: 0.65, ease: 'easeOut' },
  }),
};

const inputStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  fontFamily: 'Manrope, sans-serif',
  fontSize: '14px',
  padding: '14px 16px',
  width: '100%',
  outline: 'none',
  transition: 'border-color 0.3s, box-shadow 0.3s',
};

const InputField = ({ label, testId, type = 'text', value, onChange, placeholder, required }) => (
  <div>
    <label
      className="block text-xs font-manrope text-white/50 tracking-[0.15em] uppercase mb-2"
      data-testid={`label-${testId}`}
    >
      {label}{required && <span className="text-luma-purple ml-1">*</span>}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      style={inputStyle}
      onFocus={(e) => {
        e.target.style.borderColor = 'rgba(139,92,246,0.6)';
        e.target.style.boxShadow = '0 0 0 3px rgba(139,92,246,0.1)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = 'rgba(255,255,255,0.1)';
        e.target.style.boxShadow = 'none';
      }}
    />
  </div>
);

export const BookingSection = () => {
  const [form, setForm] = useState({
    name: '', email: '', event_type: '', event_date: '', message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type) {
      toast.error('Please fill in the required fields.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/booking`, form);
      setSubmitted(true);
      toast.success('Your request has been received. We will be in touch shortly.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-28 lg:py-36 px-6 overflow-hidden"
      style={{ background: '#0A0A0A' }}
    >
      {/* Glow accent */}
      <div
        className="absolute top-0 right-0 w-96 h-96 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left - Copy */}
          <div>
            <motion.p
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp}
              className="text-xs font-manrope tracking-[0.3em] uppercase text-luma-purple mb-4"
            >
              Get a Quote
            </motion.p>
            <motion.h2
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={1}
              className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-white mb-8"
              data-testid="booking-headline"
            >
              Custom experiences
              <br />
              tailored to your
              <br />
              <span className="gradient-text">event.</span>
            </motion.h2>
            <motion.p
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={2}
              className="font-manrope text-sm text-white/50 leading-relaxed max-w-md"
            >
              Every Luma installation is designed from scratch. Share your vision — our team will create a bespoke LED experience that becomes the unforgettable centerpiece of your event.
            </motion.p>

            {/* Feature list */}
            <motion.ul
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={3}
              className="mt-8 space-y-3"
            >
              {['Full design & installation service', 'Reactive programming to your music', 'Fully customizable visuals', '24/7 on-site support'].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-luma-purple flex-shrink-0" />
                  <span className="font-manrope text-sm text-white/55">{item}</span>
                </li>
              ))}
            </motion.ul>
          </div>

          {/* Right - Form */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={1}
          >
            {submitted ? (
              <motion.div
                data-testid="booking-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="glass-card rounded-2xl p-10 text-center"
                style={{ border: '1px solid rgba(139,92,246,0.3)' }}
              >
                <CheckCircle size={48} className="text-luma-purple mx-auto mb-6" />
                <h3 className="font-outfit font-bold text-2xl text-white mb-3">Request Received</h3>
                <p className="font-manrope text-sm text-white/55 leading-relaxed">
                  Thank you for your interest in Luma. Our team will review your request and be in touch within 24 hours.
                </p>
                <button
                  data-testid="booking-reset-btn"
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', event_type: '', event_date: '', message: '' }); }}
                  className="mt-8 text-xs font-manrope text-luma-purple hover:text-white transition-colors duration-200 tracking-[0.15em] uppercase"
                >
                  Submit another request
                </button>
              </motion.div>
            ) : (
              <form
                data-testid="booking-form"
                onSubmit={handleSubmit}
                className="glass-card rounded-2xl p-8 lg:p-10 space-y-5"
                style={{ border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <InputField
                    label="Your Name"
                    testId="input-name"
                    value={form.name}
                    onChange={handleChange('name')}
                    placeholder="Full name"
                    required
                  />
                  <InputField
                    label="Email Address"
                    testId="input-email"
                    type="email"
                    value={form.email}
                    onChange={handleChange('email')}
                    placeholder="you@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-manrope text-white/50 tracking-[0.15em] uppercase mb-2">
                    Event Type <span className="text-luma-purple">*</span>
                  </label>
                  <select
                    data-testid="input-event-type"
                    value={form.event_type}
                    onChange={handleChange('event_type')}
                    required
                    style={{ ...inputStyle, cursor: 'pointer' }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'rgba(139,92,246,0.6)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139,92,246,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <option value="" style={{ background: '#111' }}>Select event type</option>
                    {eventTypeOptions.map((opt) => (
                      <option key={opt} value={opt} style={{ background: '#111' }}>{opt}</option>
                    ))}
                  </select>
                </div>

                <InputField
                  label="Event Date (Optional)"
                  testId="input-event-date"
                  type="date"
                  value={form.event_date}
                  onChange={handleChange('event_date')}
                />

                <div>
                  <label className="block text-xs font-manrope text-white/50 tracking-[0.15em] uppercase mb-2">
                    Tell Us About Your Vision
                  </label>
                  <textarea
                    data-testid="input-message"
                    value={form.message}
                    onChange={handleChange('message')}
                    placeholder="Describe your event, space, and the experience you're after..."
                    rows={4}
                    style={{ ...inputStyle, resize: 'vertical' }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'rgba(139,92,246,0.6)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139,92,246,0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>

                <button
                  data-testid="booking-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full text-white font-manrope font-semibold text-sm px-8 py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? (
                    <><Loader size={16} className="animate-spin" /> Processing...</>
                  ) : (
                    <><Send size={16} /> Request a Quote</>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
